import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { parseApiError } from "@/utils/errorHandler";
import * as dashboardService from "@/api/dashboardService";
import type { parsedApiError } from "@/types/api/errors.api";
import type { dashboard, simulation, useDashboardParams } from "./types.api";

export const useDashboard = ({
    studentId,
    institutionId,
}: useDashboardParams = {}) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const simulationIdFromUrl = queryParams.get("simulationId");

    const [simulations, setSimulations] = useState<simulation[]>([]);
    const [dashboardData, setDashboardData] = useState<dashboard | null>(null);
    const [selectedSimulationId, setSelectedSimulationId] = useState<
        string | number | null
    >(simulationIdFromUrl);

    const [hasData, setHasData] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<parsedApiError | null>(null);

    const [activeSection, setActiveSection] = useState("results");

    useEffect(() => {
        const fetchSimulations = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await dashboardService.getSimulations(
                    studentId,
                    institutionId,
                );
                setSimulations(data);
                if (data.length > 0 && !simulationIdFromUrl) {
                    handleSimulationChange(data[0].id);
                } else if (simulationIdFromUrl) {
                    setSelectedSimulationId(simulationIdFromUrl);
                }
            } catch (e) {
                setError(parseApiError(e));
            } finally {
                setIsLoading(false);
            }
        };
        fetchSimulations();
    }, [studentId, institutionId]);

    useEffect(() => {
        if (!selectedSimulationId) {
            setDashboardData(null);
            return;
        }

        const fetchDashboardData = async () => {
            setIsLoading(true);
            setError(null);
            setHasData(true);
            try {
                const data = await dashboardService.getDashboardData(
                    selectedSimulationId,
                    studentId,
                    institutionId,
                );
                setDashboardData(data);

                if (data && data.student_answers_id === null) {
                    setHasData(false);
                }
            } catch (e) {
                setError(parseApiError(e));
            } finally {
                setIsLoading(false);
            }
        };
        fetchDashboardData();
    }, [selectedSimulationId, studentId, institutionId]);

    const handleSimulationChange = useCallback(
        (simId: string | number) => {
            const newParams = new URLSearchParams(location.search);
            newParams.set("simulationId", String(simId));
            navigate(`${location.pathname}?${newParams.toString()}`, {
                replace: true,
            });
            setSelectedSimulationId(simId);
        },
        [navigate, location],
    );

    const handleNavigation = useCallback((sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            setActiveSection(sectionId);
        }
    }, []);

    const handleSaveRecommendations = useCallback(
        async (newRecommendations: string[]) => {
            if (!dashboardData?.student_answers_id) return;

            try {
                await dashboardService.updateRecommendations(
                    dashboardData.student_answers_id,
                    newRecommendations,
                );
                setDashboardData((prev) =>
                    prev ? { ...prev, recommendations: newRecommendations } : null,
                );
            } catch (e) {
                setError(parseApiError(e));
            }
        },
        [dashboardData],
    );

    const selectedSimulation = useMemo(
        () =>
            simulations.find((sim) => sim.id === Number(selectedSimulationId)) ||
            null,
        [selectedSimulationId, simulations],
    );

    const canEditRecommendations = useMemo(
        () => user?.role === "Facilitador" && !!studentId,
        [user, studentId],
    );

    return {
        simulations,
        dashboardData,
        selectedSimulation,
        activeSection,
        isLoading,
        error,
        hasData,
        canEditRecommendations,
        handleSimulationChange,
        handleNavigation,
        handleSaveRecommendations,
    };
};
